import { Array, Boolean, Literal, Null, Number, Optional, Record, Static, String, Undefined } from 'runtypes';

import { ArchivButtonRunType } from 'src/models/archiv';
import { GlobalizationButtonRunType } from 'src/models/globalization';
import { IdoButtonRunType } from 'src/models/ido';
import { MailDetailExtrasRunType } from 'src/models/mail/extras';
import { SpoingButtonRunType } from 'src/models/spoing';
import { StatusButtonRunType } from 'src/models/status';
import { TaskButtonRunType } from 'src/models/task';

const ReceiverAddressesRunType = Record({
	receiver: String,
	receiverRealName: String,
	state: String,
});

export const MailModelAttachmentRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	attachmentId: String,
	'content-disposition': String,
	'content-id': String,
	'content-type': String,
	deletedAtTimestamp: Null,
	downloadAggregateId: String,
	fileSize: String,
	fileType: String,
	filename: String,
	isDeleted: Boolean,
	storageKey: String.Or(Null),
});

export type MailModelAttachment = Static<typeof MailModelAttachmentRunType>;
export const MailRunType = Record({
	aggregateId: String,
	aggregateVersion: Number.Or(Undefined),
	archivButton: ArchivButtonRunType.Or(Undefined),
	contactPicture: Null.Or(String).Or(Undefined),
	contactPictureVersion: Number.Or(String),
	direction: String.Or(Undefined),
	extras: MailDetailExtrasRunType.Or(Undefined),
	globalizationButton: GlobalizationButtonRunType.Or(Undefined),
	idoButton: IdoButtonRunType,
	mailModelAttachments: Array(MailModelAttachmentRunType).Or(Undefined),
	readableContentHTML: String.Or(Undefined),
	receiverAddressesBCC: Array(ReceiverAddressesRunType).Or(Undefined),
	receiverAddressesCC: Array(ReceiverAddressesRunType).Or(Undefined),
	receiverAddressesTo: Array(ReceiverAddressesRunType).Or(Undefined),
	senderAddress: String.Or(Undefined),
	senderRealName: String.Or(Undefined),
	spoingButton: SpoingButtonRunType.Or(Undefined),
	statusButton: StatusButtonRunType.Or(Undefined),
	subject: String.Or(Undefined),
	taskButton: TaskButtonRunType.Or(Undefined),
	timestamp: String.Or(Undefined),
});

export type Mail = Static<typeof MailRunType>;

export const MailAttachmentRunType = Record({});

export const MailAttachmentPreviewRunType = Record({});

export type MailAttachmentPreview = Static<typeof MailAttachmentPreviewRunType>;

export const MailSourceContentRunType = Record({
	filename: String,
	sourceBase64: String,
});

export type MailSourceContent = Static<typeof MailSourceContentRunType>;

export const MailUnprocessedContentRunType = Record({
	unprocessedContentHTML: String,
});

export type MailUnprocessedContent = Static<typeof MailUnprocessedContentRunType>;

const MailAddressSuggestIdoRunType = Record({
	idoID: String,
	instanceID: String,
});

export const MailAddressSuggestRunType = Record({
	email: String,
	groupid: Optional(String),
	ido: Optional(Array(MailAddressSuggestIdoRunType)),
	name: String,
	score: Number.Or(Null),
	source: Literal('IDO').Or(Literal('MAIL')).Or(Literal('RECEIVERGROUP')),
});

const MailReceiverGroupIdRunType = String.Or(Number);

export const MailReceiverGroupRunType = Record({
	id: MailReceiverGroupIdRunType,
	memberCount: Optional(Number),
	name: String,
	token: Optional(String),
});

export type MailReceiverGroup = Static<typeof MailReceiverGroupRunType>;

export const MailReceiverGroupMemberRunType = Record({
	emailAddress: String,
	id: MailReceiverGroupIdRunType,
});
export type MailReceiverGroupMember = Static<typeof MailReceiverGroupMemberRunType>;

export const MailReceiverGroupDetailsRunType = Record({
	id: MailReceiverGroupIdRunType,
	members: Array(MailReceiverGroupMemberRunType),
	name: String,
	token: Optional(String),
});

export type MailReceiverGroupDetails = Static<typeof MailReceiverGroupDetailsRunType>;
export type MailAddressSuggest = Static<typeof MailAddressSuggestRunType>;

export const MailSendingAccountRunType = Record({
	email: String,
	id: String,
	isTheStandardMailAccount: Boolean,
	name: String,
	standardTemplateId: Number,
	virtual: String,
});

export type MailSendingAccount = Static<typeof MailSendingAccountRunType>;

export const MailDraftOrTemplateRunType = Record({
	ID: String,
	Name: String,
	Time: String.Or(Null),
});

export type MailDraftOrTemplate = Static<typeof MailDraftOrTemplateRunType>;

export const MailSendingAccountsAndSignaturesAndDraftsAndTemplatesRunType = Record({
	autoDrafts: Array(MailDraftOrTemplateRunType),
	drafts: Array(MailDraftOrTemplateRunType),
	globalDrafts: Array(MailDraftOrTemplateRunType),
	globalTemplates: Array(MailDraftOrTemplateRunType),
	sendingAccounts: Array(MailSendingAccountRunType),
	templates: Array(MailDraftOrTemplateRunType),
});

export type MailSendingAccountsAndSignaturesAndDraftsAndTemplates = Static<
	typeof MailSendingAccountsAndSignaturesAndDraftsAndTemplatesRunType
>;

export const MailUploadedFileRunType = Record({
	attachmentId: String.Or(Null),
	filename: String,
	originalAttachmentId: String.Or(Null),
});

export type MailUploadedFile = Static<typeof MailUploadedFileRunType>;

const MailTemplateOrDraftAttachmentRunType = Record({
	AttachmentID: String,
	Disposition: String,
	Filename: String,
	Filesize: Number.Or(Null).Or(String),
	Source: String,
});

const MailTemplateOrDraftRunType = Record({
	AttachmentFromDMS: Array(MailTemplateOrDraftAttachmentRunType),
	AttachmentSourceIDs: Array(String),
	Attachments: Array(MailTemplateOrDraftAttachmentRunType),
	Content: String,
	ForwardMailID: String.Or(Number).Or(Null),
	ID: String.Or(Null),
	ManagedSignatureId: Optional(Number.Or(String).Or(Null)),
	ReceiverBCC: String,
	ReceiverCC: String,
	ReceiverTO: String,
	ReplyMailID: String.Or(Number).Or(Null),
	SourceType: String.Or(Null),
	Subject: String,
	TemplateName: String.Or(Null),
});

export const MailTemplateOrDraftLoadRunType = Record({
	templateOrDraft: MailTemplateOrDraftRunType,
	templateOrDraftType: String,
});

export type MailTemplateOrDraftLoaded = Static<typeof MailTemplateOrDraftLoadRunType>;
